import axios, { AxiosResponse } from 'axios';

/**
 * Hand-written runtime shared by the generated API client (ringil-api.v2.tsx) and the
 * ApiContext Provider (api.tsx). It holds only framework-agnostic helpers and types —
 * anything that needs React/Auth0 hooks lives in api.tsx instead.
 *
 * Keeping these here (rather than in api.tsx) avoids a circular import: api.tsx imports
 * ringilApiGenerator from the generated file, and the generated file imports Callbacks
 * from here.
 */

export interface ApiError {
  message: string;
  name: string;
  stack: string;
  config: {
    url: string;
    method: string;
    data: string;
    headers: { [key: string]: string };
  };
}

export interface Callbacks {
  /**
   * Called when the request fails (network error, 4xx/5xx, etc.).
   *
   * When omitted, the standard error toast is shown automatically. When provided, the
   * default toast is suppressed so the caller is in full control — but `showStandardError`
   * is passed in so the caller can still trigger the standard toast (with all its standard
   * items: method name, url, payload) while also doing its own work, e.g. resetting a
   * loading state:
   *
   *   onError: (error, showStandardError) => {
   *     showStandardError();
   *     setIsSubmitting(false);
   *   }
   */
  onError?: (error: ApiError, showStandardError: () => void) => void;
  onConflict?: (conflictInfo: any) => void;
  onValidationFailed?: (originalResponse: any) => void;
  onEntityNotFound?: (originalResponse: any) => void;
  onAdditionalAction?: (additionalAction: any) => void;
  /**
   * Override the automatic green "success" toast for this call. By default a successful
   * data-update (POST/PUT/PATCH/DELETE not in SUCCESS_TOAST_DENYLIST) shows a generic
   * confirmation toast so the user knows their change was performed.
   *
   *   absent (default)   → automatic behaviour (toast iff a data-update endpoint)
   *   false              → suppress the toast for this call (caller shows its own, or it's silent)
   *   true               → force the generic toast (e.g. for an endpoint that's in the denylist)
   *   { summary, detail } → show the toast with custom wording
   *
   * The toast fires only on a genuine success (isSuccess) — never on
   * conflict/validation/additional-action/not-found/error — and never affects onSuccess:
   * your loading-flag/navigation/reload logic always runs exactly as before.
   */
  successToast?: boolean | { summary?: string; detail?: string };
}

/**
 * Signature of the request executor injected into the generated client. The generated
 * apiMethods are thin wrappers that only call this — the actual transport, auth and
 * token-refresh logic lives in api.tsx.
 */
export type ExecuteRequest = (
  method: string,
  url: string,
  data: any,
  params: any[],
  isPublic: boolean,
  isBinary: boolean,
  methodName: string,
  onSuccess: (data: any) => void,
  callbacks: Callbacks,
  hashAuthentication?: string,
) => void;

const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Merges all per-call query-param objects into a single object and always adds the
 * tracing params (trcRequestDate, trcUUID) plus the optional admin runAs impersonation.
 */
export const resolveQueryParams = (params: any[], runAs?: string): Record<string, any> => {
  const retObject: Record<string, any> = {
    trcRequestDate: new Date().toISOString(),
    trcUUID: generateUUID(),
    runAs: runAs !== undefined ? runAs : null,
  };
  params.forEach(item => {
    if (item == null) return;
    Object.keys(item).forEach(key => {
      retObject[key] = item[key];
    });
  });
  return retObject;
};

export const isSuccess = (response: AxiosResponse): boolean => {
  return response.status >= 200 && response.status < 300 && response.data.errorType == null;
};

export const hasAdditionalAction = (response: AxiosResponse): boolean => {
  return response.status >= 200 && response.status < 300 && response.data.errorType === 'ADDITIONAL_ACTION';
};

export const isConflict = (response: AxiosResponse): boolean => {
  return response.status >= 200 && response.status < 300 && response.data.errorType === 'CONFLICT';
};

export const validationFailed = (response: AxiosResponse): boolean => {
  return response.status === 200 && response.data.errorType === 'VALIDATION';
};

export const entityNotFound = (response: AxiosResponse): boolean => {
  return response.status === 204;
};

/**
 * Routes a (non-binary) response to onSuccess or the appropriate callback based on the
 * backend's errorType envelope.
 */
export const handleResponse = (response: any, onSuccess: (data: any) => void, callbacks: Callbacks = {}): void => {
  // `= {}` only guards `undefined`; an explicit `null` from a caller would crash on `callbacks.<x>`.
  callbacks = callbacks ?? {};
  if (isSuccess(response)) {
    onSuccess(response.data.payload);
  }
  if (hasAdditionalAction(response)) {
    callbacks.onAdditionalAction?.(response.data.payload);
  }
  if (isConflict(response)) {
    callbacks.onConflict?.(response.data.payload.entity);
  }
  if (validationFailed(response)) {
    callbacks.onValidationFailed?.(response.data.messages);
  }
  if (entityNotFound(response)) {
    callbacks.onEntityNotFound?.(response.data.payload);
  }
};

export const handleCatchError = (
  name: string,
  error: any,
  callbacks: Callbacks = {},
  handleError: (methodName: string, error: ApiError, payload?: any) => any,
  payload?: any,
): void => {
  // `= {}` only guards `undefined`; an explicit `null` from a caller would crash on `callbacks.onError`.
  callbacks = callbacks ?? {};
  // A canceled request (component unmount / navigation, or an aborted in-flight call) is not a
  // real failure — never toast or report it. This is the main source of the spurious error
  // popups users saw while clicking between pages.
  if (axios.isCancel(error) || (error as any)?.code === 'ERR_CANCELED') {
    return;
  }
  if (callbacks.onError) {
    callbacks.onError(error, () => handleError(name, error, payload));
  } else {
    handleError(name, error, payload);
  }
};